import { Medication, MEDICATION_FREQUENCIES } from '../types';

interface MedicationListProps {
  medications: Medication[];
  onChange: (medications: Medication[]) => void;
}

export default function MedicationList({ medications, onChange }: MedicationListProps) {
  const addMedication = () => {
    const newMedication: Medication = {
      id: crypto.randomUUID(),
      name: '',
      dose: '',
      frequency: ''
    };
    onChange([...medications, newMedication]);
  };

  const updateMedication = (id: string, field: keyof Omit<Medication, 'id'>, value: string) => {
    onChange(
      medications.map(med =>
        med.id === id ? { ...med, [field]: value } : med
      )
    );
  };

  const removeMedication = (id: string) => {
    onChange(medications.filter(med => med.id !== id));
  };

  return (
    <div className="space-y-3">
      {medications.length === 0 ? (
        <p className="text-gray-500 text-sm italic">No medications added</p>
      ) : (
        <div className="space-y-3">
          {medications.map((medication, index) => (
            <div
              key={medication.id}
              className="flex flex-wrap gap-2 items-start p-3 bg-gray-50 rounded-lg border border-gray-200"
            >
              <span className="text-sm font-medium text-gray-500 w-6 pt-2">
                {index + 1}.
              </span>

              <div className="flex-1 min-w-[150px]">
                <label className="text-xs text-gray-500 mb-1 block">Medication</label>
                <input
                  type="text"
                  value={medication.name}
                  onChange={(e) => updateMedication(medication.id, 'name', e.target.value)}
                  placeholder="Medication name"
                  className="input-field text-sm"
                />
              </div>

              <div className="w-28">
                <label className="text-xs text-gray-500 mb-1 block">Dose</label>
                <input
                  type="text"
                  value={medication.dose}
                  onChange={(e) => updateMedication(medication.id, 'dose', e.target.value)}
                  placeholder="e.g., 50mg"
                  className="input-field text-sm"
                />
              </div>

              <div className="w-40">
                <label className="text-xs text-gray-500 mb-1 block">Frequency</label>
                <select
                  value={medication.frequency}
                  onChange={(e) => updateMedication(medication.id, 'frequency', e.target.value)}
                  className="select-field text-sm"
                >
                  <option value="">Select...</option>
                  {MEDICATION_FREQUENCIES.map(freq => (
                    <option key={freq} value={freq}>{freq}</option>
                  ))}
                </select>
              </div>

              <button
                type="button"
                onClick={() => removeMedication(medication.id)}
                className="mt-5 p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                title="Remove medication"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}

      <button
        type="button"
        onClick={addMedication}
        className="flex items-center gap-2 text-clinical-600 hover:text-clinical-700 font-medium text-sm mt-2"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
        </svg>
        Add Medication
      </button>
    </div>
  );
}
